"use client";

import { useEffect, useState } from "react";
import { AlertCircle, Calculator, ExternalLink, FileText, Loader2 } from "lucide-react";

import { Button } from "@/app/_module/components/ui/button";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import { useFeatureFlag } from "@/app/_module/hooks/useFeatureFlag";
import techficientService, {
  type TechficientCaseRow,
  type TechficientQuoteRequestPayload,
} from "@/app/_module/services/techficientService";
import { EAPP_POPUP_CLOSED_EVENT } from "@/app/_module/contexts/EnrollmentFloaterContext";
import { useResumeEappCase } from "@/app/_module/hooks/useResumeEappCase";
import TechficientQuoteResultCard from "@/app/_module/components/insurabot-cards/TechficientQuoteResultCard";
import { leadToPreset } from "@/app/(authenticated)/quote-enroll/_modules/components/QuoteForm";

/**
 * "Instant Quotes" — lives under Recent Activity on lead details. Shows the
 * lead's multi-carrier quote tiles (with Apply) without the agent opening
 * the Quote tab:
 *
 *   1. A stored quote for this lead exists → render it instantly (no
 *      carrier-API cost, no new history row).
 *   2. No quote yet but the lead card has everything the rater needs
 *      (DOB / gender / state / ZIP / height / weight) → auto-run ONE quote
 *      with sensible defaults (Term $250K·20yr, or Final Expense $15K when
 *      the applicant is 60+) and show it. Runs at most once per mount.
 *   3. Lead is missing rating data → quiet hint, no API call.
 *
 * Refresh re-runs with the same parameters as the quote on screen.
 */
export function LeadInstantQuotes({
  leadId,
  lead,
}: {
  leadId?: string;
  lead?: any;
}) {
  const { enabled: quoteEnrollEnabled } = useFeatureFlag("quoteAndEnroll");
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [display, setDisplay] = useState<{
    quotedAt: string | null;
    payload: any;
    quoteRequestId: string;
    results: any[];
  } | null>(null);
  const [cases, setCases] = useState<TechficientCaseRow[]>([]);
  const { resumeCase, viewCase } = useResumeEappCase();

  // Applications & Cases scoped to THIS lead — so the agent can resume an
  // in-flight eApp right from the lead card. Reloads when a carrier popup
  // closes (status may have advanced).
  const loadCases = async () => {
    if (!leadId) return;
    try {
      const rows = await techficientService.listCases({ leadId });
      setCases(Array.isArray(rows) ? rows : []);
    } catch {
      /* quiet — cases strip just stays hidden */
    }
  };
  useEffect(() => {
    if (!quoteEnrollEnabled || !leadId) return;
    void loadCases();
    const onPopupClosed = () => void loadCases();
    window.addEventListener(EAPP_POPUP_CLOSED_EVENT, onPopupClosed);
    return () =>
      window.removeEventListener(EAPP_POPUP_CLOSED_EVENT, onPopupClosed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteEnrollEnabled, leadId]);

  const preset = leadToPreset(lead);

  const presetMissing = (): string[] => {
    const m: string[] = [];
    if (!preset.birthDate) m.push("DOB");
    if (!preset.gender) m.push("gender");
    if (!preset.state) m.push("state");
    if (!preset.zipCode) m.push("ZIP");
    if (preset.heightInches == null) m.push("height");
    if (preset.weightPounds == null) m.push("weight");
    return m;
  };

  const buildDefaultRequest = (): TechficientQuoteRequestPayload => {
    const age = preset.birthDate
      ? Math.floor(
          (Date.now() - new Date(preset.birthDate).getTime()) /
            (365.25 * 24 * 3600 * 1000),
        )
      : 40;
    const isFE = age >= 60;
    return {
      amount: isFE ? 15_000 : 250_000,
      class: "PHC",
      term: isFE ? null : 20,
      gender: preset.gender as "M" | "F",
      birthDate: preset.birthDate as string,
      state: preset.state as string,
      adbRider: false,
      wopRider: false,
      childRider: "ZERO",
      productTypeFilter: isFE ? "FinalExpense" : "Term",
      mode: "Monthly",
      preUnderwritingRequest: {
        heightInInches: preset.heightInches,
        weight: preset.weightPounds,
        ...(preset.tobaccoUse != null ? { tobacco: preset.tobaccoUse ? "current" : "never" } : {}),
      },
    } as TechficientQuoteRequestPayload;
  };

  const runQuote = async (request: TechficientQuoteRequestPayload) => {
    if (!leadId) return;
    setRunning(true);
    setError(null);
    try {
      const response = await techficientService.runQuote({
        request,
        leadId,
        ...(preset.leadName ? { clientName: preset.leadName } : {}),
        ...(preset.zipCode ? { zipCode: preset.zipCode } : {}),
      });
      setDisplay({
        quotedAt: new Date().toISOString(),
        payload: request,
        quoteRequestId: response.quoteRequest.techficientQuoteRequestId,
        results: response.results || [],
      });
    } catch (e: any) {
      const raw = String(e?.response?.data?.message || e?.message || "");
      // Feature-flag race / non-entitled agency → just hide the box.
      if (e?.response?.status === 403 || /not enabled/i.test(raw)) {
        setDisplay(null);
        setError(null);
      } else {
        setError(
          raw.replace(/techficient|techf\.com/gi, "carrier") ||
            "Couldn't build quotes — try refresh.",
        );
      }
    } finally {
      setRunning(false);
    }
  };

  // Initial load: stored quote first; auto-run once only when none exists.
  useEffect(() => {
    if (!quoteEnrollEnabled || !leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res: any = await techficientService.listQuotesForLead(leadId);
        if (cancelled) return;
        const items: any[] = res?.items || res?.data?.items || [];
        const latest = items.find(
          (q: any) => (q.results || []).length > 0,
        );
        if (latest) {
          setDisplay({
            quotedAt: latest.createdAt || null,
            payload: latest.requestPayload || {},
            quoteRequestId: latest.techficientQuoteRequestId,
            results: latest.results || [],
          });
        }
        // No stored quote → the box simply doesn't render. Quotes are run
        // deliberately (Quote tab / Quote & Enroll), never auto-fired from
        // opening a lead.
      } catch {
        if (!cancelled) setDisplay(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteEnrollEnabled, leadId]);

  if (!quoteEnrollEnabled || !leadId) return null;
  // Only surface the box once a quote (or an application) exists for this
  // lead — a lead that's never been quoted shows nothing here, including
  // while we're still finding out (no skeleton flash).
  if (!display && cases.length === 0) return null;

  // Kept for the error-state retry — the box otherwise always shows the
  // latest stored quote on load, so there's no header Refresh button.
  const refresh = () => {
    void loadCases();
    if (!display && presetMissing().length > 0) return;
    // Same parameters as the quote on screen; defaults when starting fresh.
    const p = display?.payload;
    const request: TechficientQuoteRequestPayload =
      p && p.amount && p.birthDate
        ? ({
            amount: Number(p.amount),
            class: p.class || "PHC",
            term: p.term ?? null,
            gender: p.gender,
            birthDate: p.birthDate,
            state: p.state,
            adbRider: !!p.adbRider,
            wopRider: !!p.wopRider,
            childRider: p.childRider || "ZERO",
            productTypeFilter: p.productTypeFilter || "Term",
            mode: p.mode || "Monthly",
            ...(p.preUnderwritingRequest
              ? { preUnderwritingRequest: p.preUnderwritingRequest }
              : {}),
          } as TechficientQuoteRequestPayload)
        : buildDefaultRequest();
    void runQuote(request);
  };

  const payload = display?.payload || {};
  const summary = [
    preset.leadName,
    payload.amount
      ? `$${Number(payload.amount) >= 1_000_000 ? `${(Number(payload.amount) / 1_000_000).toFixed(1)}M` : `${Math.round(Number(payload.amount) / 1000)}K`}`
      : null,
    payload.productTypeFilter === "FinalExpense"
      ? "Final Expense"
      : payload.term
        ? `${payload.term}-yr term`
        : null,
    payload.state || preset.state,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Card className="shadow-sm border-gray-200">
      <CardContent className="p-6">
      <div className="flex items-center gap-3 mb-1">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Calculator className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-gray-900">Quotes</h3>
          {display?.quotedAt && (
            <p className="text-xs text-gray-500 mt-0.5">
              Quoted {new Date(display.quotedAt).toLocaleDateString()} — Apply
              starts the application for this lead.
            </p>
          )}
        </div>
      </div>

      {loading || running ? (
        <div className="mt-3 flex items-center gap-2 text-xs text-gray-500 py-6 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" />
          {running ? "Building quotes across your carriers…" : "Loading…"}
        </div>
      ) : display ? (
        <TechficientQuoteResultCard
          data={{
            quoteRequestId: display.quoteRequestId,
            leadId,
            applicantSummary: summary,
            coverageType: payload.productTypeFilter || "Term",
            termYears: payload.term ?? null,
            faceAmount:
              payload.amount != null ? Number(payload.amount) : undefined,
            state: payload.state || preset.state || undefined,
            results: (display.results || []).map((r: any) => ({
              quoteRequestResultId: r.techficientQuoteRequestResultId,
              carrier: r.carrierName,
              product: r.productName,
              monthlyPremium: r.monthlyPremium,
              annualPremium: r.annualPremium,
              healthClass: r.healthClassDisplayName,
              instant: r.isInstantIssue,
              supportsAppSubmit: r.supportsAppSubmit,
              submittableType:
                r.submittableType ||
                (r.supportsAppSubmit ? "full_eapp" : "quote_only"),
            })),
          }}
        />
      ) : error ? (
        <div className="mt-3 flex items-start gap-2 rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span className="flex-1">{error}</span>
          <button
            type="button"
            onClick={refresh}
            className="font-semibold text-rose-800 hover:underline flex-shrink-0"
          >
            Try again
          </button>
        </div>
      ) : null}

      {/* Applications & Cases for THIS lead — resume in-flight eApps
          without leaving the lead card. */}
      {cases.length > 0 && (
        <div className="mt-4 border-t pt-3">
          <p className="text-xs font-semibold text-gray-700 mb-2 inline-flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5 text-gray-400" />
            Applications &amp; Cases
          </p>
          <div
            className="space-y-2 max-h-72 overflow-y-auto pr-1"
            style={{ scrollbarWidth: "thin" }}
          >
            {cases.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between gap-3 rounded-md border bg-gray-50/60 px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-gray-900 truncate">
                    {c.carrierName || "Carrier"}
                    {c.productName ? ` · ${c.productName}` : ""}
                  </p>
                  <p className="text-[11px] text-gray-500 truncate">
                    {[
                      c.caseStatusName || c.caseStatus,
                      c.appliedFaceAmount
                        ? `$${Math.round(Number(c.appliedFaceAmount) / 1000)}K`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                  {c.policyNumber && (
                    <span className="mt-1 inline-flex items-center gap-1 rounded bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800 tabular-nums">
                      <FileText className="h-2.5 w-2.5 text-emerald-600" />
                      Policy #{c.policyNumber}
                    </span>
                  )}
                </div>
                {c.launchUrl && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 flex-shrink-0 gap-1.5"
                    onClick={() =>
                      c.resumable === false ? viewCase(c) : void resumeCase(c)
                    }
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    {c.resumable === false ? "View" : "Resume"}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      </CardContent>
    </Card>
  );
}

export default LeadInstantQuotes;
